import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '../../../axiosInstances/axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import {
  CountryWiseProvince,
  Province,
  ProvinceReqBody,
} from '../../../types/province';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useProvincesActions = (
  whichAction: 'provinceList' | '',
  countryName?: string,
  isoCode?: string
) => {
  // query function to fetch all the provinces from database declared here
  const getProvinceListHandler = useQuery({
    queryKey: ['province-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Province>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/province/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'provinceList'),
  });

  // query function to fetch all the provinces by country declared here
  const getCountryWiseProvincesHandler = useQuery({
    queryKey: ['country-wise-province-list', countryName],
    queryFn: async (): Promise<
      AcademiaResponse<Array<CountryWiseProvince>>
    > => {
      const response = await getPrivateAxiosInstance('user').get(
        `/country/wise/provinces/${countryName}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!countryName),
  });

  // query function to get added provinces by ISO code declared here
  const getCountryWiseAddedProvincesHandler = useQuery({
    queryKey: ['country-wise-selected-provinces', isoCode],
    queryFn: async (): Promise<AcademiaResponse<Array<Province>>> => {
      const response = await getAxiosInstance('user').get(
        `/provinces/with/${isoCode}/`
      );

      return response.data;
    },
    ...returnQueryConfig(!!isoCode),
  });

  // mutation function to add new province to the database declared here
  const addNewProvinceHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: Array<ProvinceReqBody>;
    }): Promise<AcademiaResponse<Province>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/province/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing province by id declared here
  const updateProvinceByIdHandler = useMutation({
    mutationFn: async ({
      provinceId,
      body,
    }: {
      provinceId: number;
      body: ProvinceReqBody;
    }): Promise<AcademiaResponse<Province>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/province/${provinceId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete province by id declared here
  const deleteProvinceByIdHandler = useMutation({
    mutationFn: ({
      provinceId,
    }: {
      provinceId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/province/${provinceId}/delete/`),
  });

  return {
    getProvinceListHandler,
    getCountryWiseProvincesHandler,
    getCountryWiseAddedProvincesHandler,
    addNewProvinceHandler,
    updateProvinceByIdHandler,
    deleteProvinceByIdHandler,
  };
};

export default useProvincesActions;
